/**
 * @file BillingAnomaliesReport.tsx
 * @brief Super Admin report — payment failures, unpaid seats, pool overage.
 *
 * @references
 * - GET /api/admin/reports/billing-anomalies
 * - docs/product/reporting.md (sa-billing-anomalies)
 *
 * @author DES
 * @date 27-07-2026
 */

import { useMemo, useState } from "react";
import {
  CreditCard,
  FileExclamationPoint,
  Gauge,
  TriangleAlert,
} from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { StatCard } from "@/features/analytics/components/StatCard";
import type { AnalyticsRange as RangeBarValue } from "@/features/analytics/components/AnalyticsRangeBar";
import {
  ReportKpiCell,
  ReportKpiRow,
  ReportSectionHeader,
  ReportTableCell,
} from "@/features/reports-shared/ReportSection";
import {
  formatDateTime,
  formatMoneyMinor,
} from "@/shared/utils/formatters";
import { useBillingAnomaliesReport } from "../api";
import { ReportShell } from "./ReportShell";
import { ReportTable } from "./ReportTable";

function defaultRange(): RangeBarValue {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
    groupBy: "day",
  };
}

function kindBadge(kind: string) {
  switch (kind) {
    case "payment_failed":
      return <Badge variant="destructive">Payment failed</Badge>;
    case "unpaid_invoice":
      return <Badge variant="warning">Unpaid invoice</Badge>;
    case "incomplete_subscription":
      return <Badge variant="warning">Incomplete seat</Badge>;
    case "pool_overage":
      return <Badge variant="info">Pool overage</Badge>;
    default:
      return <Badge variant="muted">{kind}</Badge>;
  }
}

export function BillingAnomaliesReport() {
  const [range, setRange] = useState<RangeBarValue>(() => defaultRange());
  const [companyId, setCompanyId] = useState<string | null>(null);

  const queryRange = useMemo(
    () => ({
      startDate: range.startDate,
      endDate: range.endDate,
      companyId: companyId ?? undefined,
    }),
    [range.startDate, range.endDate, companyId],
  );

  const report = useBillingAnomaliesReport(queryRange);
  const summary = report.data?.summary;
  const rows = report.data?.anomalies ?? [];

  return (
    <ReportShell
      title="Billing anomalies"
      description="Failed charges, unpaid plan invoices, incomplete seats, and Enterprise pool overage. Incomplete seats stand in for stuck payment setup (Setup Intents are not stored)."
      range={range}
      onRangeChange={setRange}
      showCompanyFilter
      companyId={companyId}
      onCompanyChange={setCompanyId}
      exportDisabled={rows.length === 0}
      serverExport={{
        scope: "admin",
        reportKey: "billing-anomalies",
        params: {
          startDate: range.startDate,
          endDate: range.endDate,
          companyId: companyId ?? undefined,
        },
      }}
      enablePdf
    >
      <>
        <ReportKpiRow>
          <ReportKpiCell>
            <StatCard
              variant="cell"
              tone="brand"
              label="Payment failed"
              icon={TriangleAlert}
              value={String(summary?.payment_failed ?? 0)}
              attention={Number(summary?.payment_failed ?? 0) > 0}
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
          <ReportKpiCell>
            <StatCard
              variant="cell"
              tone="warning"
              label="Unpaid invoices"
              icon={FileExclamationPoint}
              value={String(summary?.unpaid_invoice ?? 0)}
              attention={Number(summary?.unpaid_invoice ?? 0) > 0}
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
          <ReportKpiCell>
            <StatCard
              variant="cell"
              tone="warning"
              label="Incomplete seats"
              icon={CreditCard}
              value={String(summary?.incomplete_subscription ?? 0)}
              attention={Number(summary?.incomplete_subscription ?? 0) > 0}
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
          <ReportKpiCell>
            <StatCard
              variant="cell"
              tone="info"
              label="Pool overage"
              icon={Gauge}
              value={String(summary?.pool_overage ?? 0)}
              attention={Number(summary?.pool_overage ?? 0) > 0}
              hint="Active Enterprise plans"
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
        </ReportKpiRow>

        <ReportSectionHeader
          title="Anomalies"
          description={`${summary?.total ?? 0} rows (capped per category at 200).`}
        />
        <ReportTableCell>
          <ReportTable
            headers={[
              "Kind",
              "Product",
              "Company / owner",
              { label: "Amount", align: "right" },
              { label: "When", align: "right" },
              "Detail",
            ]}
            isLoading={report.isLoading}
            empty={rows.length === 0}
            emptyMessage="No anomalies for this range."
          >
            {rows.map((row, idx) => (
              <tr
                key={`${row.kind}-${row.ref_id}-${idx}`}
                className="hover:bg-secondary/20"
              >
                <td className="px-4 py-2">{kindBadge(row.kind)}</td>
                <td className="px-4 py-2">{row.product_label || "—"}</td>
                <td className="px-4 py-2">
                  <div className="font-medium">
                    {row.company_name || "—"}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {row.owner_email || row.ref_id || ""}
                  </div>
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {row.amount_cents
                    ? formatMoneyMinor(
                        row.amount_cents,
                        row.currency || "GBP",
                      )
                    : "—"}
                </td>
                <td className="px-4 py-2 text-right text-muted-foreground">
                  {formatDateTime(row.occurred_at)}
                </td>
                <td className="px-4 py-2 text-sm text-muted-foreground">
                  {row.detail || "—"}
                </td>
              </tr>
            ))}
          </ReportTable>
        </ReportTableCell>
      </>
    </ReportShell>
  );
}
